import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { acceptFinding } from '../lib/api/qg-actions.js';

interface AcceptFindingActionProps {
  articleId: string;
  issueIndex: number;
  /** The article's updated_at, for the server's version check. */
  updatedAt?: string | null;
  onAccepted?: () => void;
}

/** Accept a non-blocking finding as resolved (ruling 33).
 *
 *  For when the operator disagrees with the judge, or has already fixed the
 *  text in a way no code check can confirm. Their name and reason go on
 *  record; if nothing else holds the article, it is released and published
 *  as theirs. Offered only where the gate's own predicate says the finding
 *  does not block — a blocking one is resolved by override. */
export function AcceptFindingAction({ articleId, issueIndex, updatedAt, onAccepted }: AcceptFindingActionProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const accept = useMutation({
    mutationFn: () => acceptFinding(articleId, issueIndex, reason.trim(), updatedAt),
    onSuccess: () => onAccepted?.(),
  });

  if (accept.isSuccess) {
    return (
      <p className="text-xs text-success-700 mt-2">
        Accepted as resolved.
        {accept.data.released ? ' Nothing else held the article — released.' : ''}
      </p>
    );
  }
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold text-grey-700 border border-grey-300 hover:bg-grey-50"
        title="Your word resolves this finding, with your reason on record. If nothing else holds the article it is released and published."
      >
        <Check className="h-3.5 w-3.5" /> Accept as resolved
      </button>
    );
  }
  const ok = reason.trim().length >= 10;
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why this is resolved (at least 10 characters)"
        className="flex-1 min-w-[16rem] rounded border border-grey-300 px-2 py-1"
        aria-label="Reason for accepting the finding as resolved"
        autoFocus
      />
      <button
        type="button"
        disabled={!ok || accept.isPending}
        onClick={() => accept.mutate()}
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-success-50 text-success-700 border border-success-200 font-semibold hover:bg-success-100 disabled:opacity-50"
      >
        {accept.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-grey-500 underline">Cancel</button>
      {accept.isError && <span className="text-danger-700 w-full">{(accept.error as Error).message}</span>}
    </div>
  );
}
